import { useCallback, useEffect, useState } from "react";
import { getApiUrl } from "../../utils/api";
import { ensureFreshAccessToken } from "../../utils/auth";

/**
 * @typedef {Object} CaseTicket
 * @property {string} caseId
 * @property {string} [title]
 * @property {string} [status]
 * @property {string} [priority]
 * @property {string} [product]
 * @property {string} [namespace]
 * @property {string} [description]
 * @property {number} chunkCount
 * @property {number} chunksFound
 * @property {{chunkIndex:number,pointId:string,text:string,length:number}[]} chunks
 * @property {string[]} warnings
 */

// Per-key cache so a re-render or a remount never refetches. Failures are not cached.
const cache = new Map();
const inflight = new Map();

const load = (key) => {
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    const token = await ensureFreshAccessToken();
    const res = await fetch(getApiUrl(`/api/cases/${encodeURIComponent(key)}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 404) {
      const err = new Error("not found");
      err.notFound = true;
      throw err;
    }
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    const data = await res.json();
    cache.set(key, data);
    return data;
  })().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};

/** @returns {{data: CaseTicket|null, loading: boolean, error: string, notFound: boolean, retry: () => void}} */
export default function useCaseTicket(caseId) {
  const [state, setState] = useState({
    data: cache.get(caseId) || null,
    loading: !cache.has(caseId),
    error: "",
    notFound: false,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    if (cache.has(caseId)) {
      setState({ data: cache.get(caseId), loading: false, error: "", notFound: false });
      return undefined;
    }
    setState({ data: null, loading: true, error: "", notFound: false });
    load(caseId)
      .then((data) => live && setState({ data, loading: false, error: "", notFound: false }))
      .catch((e) =>
        live &&
        setState({
          data: null,
          loading: false,
          error: e.notFound ? "" : e.message || "Something went wrong",
          notFound: !!e.notFound,
        })
      );
    return () => {
      live = false;
    };
  }, [caseId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
